import {
  address,
  addressUtils,
  contactUtils,
  deliveryDomain,
  discountTypes,
  productDomains,
  stringUtils,
  payBoxUtils,
  quotationItemUtils,
} from '@/mixins/'
import { localListAddItem, localListDeleteItem, localListUpdateItem } from '@/store/utils'
import { roundDecimal } from '@/utils/number-utils'
import axios from '@axios'

// Seleciona a taxa da faixa aplicável (cobrança única): a faixa cujo "a partir de" é o maior valor
// menor ou igual ao valor de produtos. Sem faixas, retorna null (o chamador usa a taxa fixa).
const selectDeliveryTierValue = (tiers, productsValue) => {
  if (!Array.isArray(tiers) || tiers.length === 0) return null
  const value = productsValue || 0
  const applicable = tiers
    .filter(tier => (tier.productsValueFrom ?? 0) <= value)
    .sort((a, b) => (b.productsValueFrom ?? 0) - (a.productsValueFrom ?? 0))[0]
  return (applicable ?? tiers[0]).value
}

const getInitialState = () => ({
  quotation: {
    id: null,
    storeId: null,
    store: null,
    customerId: null,
    customer: null,
    deliveryAddress: address.data().address,
    deliveryFeeType: null,
    deliveryTaxDistance: null,
    deliveryTaxUnitValue: null,
    shippingFee: null,
    shippingFeePerKilometer: null,
    discountType: discountTypes.computed.discountTypesEnum().VALUE,
    discount: 0,
    discountAuth: null,
    priceTableId: '1',
    products: [],
    addressSelected: null,
    isDelivery: false,
    isConsigned: false,
  },
  deliveryData: {
    deliveryFeeType: deliveryDomain.data().deliveryFeeEnum.SINGLE_FEE,
    distanceValue: null,
    shippingFee: 0,
    shippingFeePerKilometer: 0,
    // Faixas de taxa (cobrança única): a taxa é a faixa cujo "a partir de" é o maior valor <= ao
    // total de produtos do orçamento (D3).
    tiers: [],
    // Grupos de produto isentos da base de cálculo do frete (D15): itens desses grupos não
    // entram no valor usado para escolher a faixa de taxa.
    excludedGroupIds: [],
    deliveryTax: 0,
    discount: 0,
  },
})

export const getInitialItemForm = () => ({
  id: null,
  localId: null,
  productKitId: null,
  isKit: false,
  skuProduct: null,
  quantity: 1,
  maxQuantity: -1,
  discountType: discountTypes.computed.discountTypesEnum().VALUE,
  promotionId: null,
  unitDiscount: 0,
  unitDiscountAuth: null,
  isDeleted: false,
  subtotal: 0,
  total: 0,
  totalDiscount: 0,
  priceTable: { id: null },
  lendingProducts: [],
  kitItems: [],
  classification: null,
  hasColdOption: false,
})

export default {
  namespaced: true,
  state: getInitialState(),

  getters: {
    // Valor líquido dos produtos (usado para escolher a faixa de taxa de delivery — D3).
    getTotalProductValue(state) {
      return state.quotation.products
        .filter(p => !p.isDeleted)
        .reduce((prev, p) => prev + (p.priceRuleSelected?.priceInfo?.localNetValue || 0), 0)
    },

    // Base de cálculo do frete por faixas (D15): soma o valor líquido apenas dos itens que NÃO
    // pertencem a grupos isentos. Degrada com segurança: sem grupos isentos, ou item sem grupo
    // conhecido, entra na base (mesmo critério do servidor em Sale.GetProductsNetValue).
    getDeliveryTierBaseValue(state) {
      const excluded = state.deliveryData.excludedGroupIds || []
      return state.quotation.products
        .filter(p => !p.isDeleted)
        .filter(p => {
          if (excluded.length === 0) return true
          const groupId = p.skuProduct?.groupId
          if (groupId == null) return true
          return !excluded.includes(groupId)
        })
        .reduce((prev, p) => prev + (p.priceRuleSelected?.priceInfo?.localNetValue || 0), 0)
    },

    getProducts(state) {
      const itemsProduct = state.quotation.products
        .filter(p => !p.isDeleted)
        .map(p => ({
          ...p,
          _showDetails: !!p.lendingProducts || p.kitItems?.length > 0,
        }))

      return itemsProduct
    },

    getCustomerAddressOptions(state) {
      return addressUtils.addressOptionsWithOther(state.quotation.customer?.addresses)
    },

    getCustomerTelephone(state) {
      return contactUtils.getPricipalMobileAndLandline(state.quotation.customer?.telephones)
    },

    getQuotationTotalProducts(state) {
      const { discountType, discount, products, isDelivery } = state.quotation

      const totalProducts = products.reduce(
        (prev, p) => prev + p.priceRuleSelected?.priceInfo?.localNetValue,
        0
      )
      const totalDelivery = isDelivery ? state.deliveryData.deliveryTax : 0

      const discountValue = payBoxUtils.getDiscountValue({
        discountType,
        discount,
        value: totalProducts,
      })
      return totalProducts + totalDelivery - discountValue
    },

    getQuotationTotalDiscount(state) {
      const { products, discountType, discount } = state.quotation

      const totalProducts = products.reduce(
        (prev, p) => prev + p.priceRuleSelected?.priceInfo?.localNetValue,
        0
      )
      const totalProductDiscount = products.reduce(
        (prev, p) => prev + p.priceRuleSelected?.priceInfo?.localDiscountTotal,
        0
      )

      const discountValue = payBoxUtils.getDiscountValue({
        discountType,
        discount,
        value: totalProducts,
      })
      return totalProductDiscount + discountValue
    },

    hasDoubleDelivery(state) {
      const { isConsigned, isDelivery } = state.quotation
      if (!isDelivery) return false
      if (isConsigned) return true

      const { RENT } = productDomains.computed.productClassificationsEnum()
      const kitItems = state.quotation.products.flatMap(p => p.kitItems || [])
      const allProducts = kitItems.concat(state.quotation.products)
      const hasLendingOrRent = allProducts.some(
        p => p.lendingProducts.length > 0 || p.skuProduct.classification === RENT
      )
      return hasLendingOrRent
    },
  },

  mutations: {
    SET_QUOTATION(state, { quotation }) {
      state.quotation = quotation
    },
    SET_QUOTATION_STORE_ID(state, { storeId }) {
      state.quotation.storeId = storeId
    },
    SET_QUOTATION_STORE(state, { store }) {
      state.quotation.store = store
    },
    SET_QUOTATION_CUSTOMER(state, { customer }) {
      const { getStringOrNull } = stringUtils.methods
      state.quotation.customerId = getStringOrNull(customer?.id)
      state.quotation.customer = customer
    },
    SET_QUOTATION_PRICE_TABLE_ID(state, { priceTableId }) {
      const { getStringOrNull } = stringUtils.methods
      state.quotation.priceTableId = getStringOrNull(priceTableId) || '1'
    },
    SET_PRODUCTS(state, { products }) {
      state.quotation.products = products
    },
    SET_DELIVERY_TAX(state, deliveryTax) {
      state.deliveryData.deliveryTax = deliveryTax
    },
    SET_DELIVERY_DATA(state, deliveryData) {
      state.deliveryData = deliveryData
    },
    // Taxa única efetiva da loja (faixa aplicada conforme o valor de produtos). Mantém o campo
    // exibido e o valor gravado (deliveryTaxUnitValue) coerentes com a faixa cobrada (D3).
    SET_DELIVERY_SINGLE_FEE(state, shippingFee) {
      state.deliveryData.shippingFee = shippingFee
    },

    SET_DELIVERY_ADDRESS(state, { addressData }) {
      state.quotation.deliveryAddress = addressData || getInitialState().quotation.deliveryAddress
    },

    CLEAN_STATE(state) {
      const { quotation, deliveryData } = getInitialState()
      state.quotation = quotation
      state.deliveryData = deliveryData
    },

    CLEAN_DELIVERY_ADDRESS(state) {
      const { deliveryAddress } = getInitialState().quotation
      state.quotation.deliveryAddress = deliveryAddress
    },

    CLEAN_INPUTS_AUTH(state) {
      const { discount, discountAuth } = getInitialState().quotation
      const { unitDiscount, unitDiscountAuth } = getInitialItemForm()
      state.quotation.discountAuth = discountAuth
      state.quotation.discount = discount
      state.quotation.products = state.quotation.products.map(p => ({
        ...p,
        unitDiscount,
        unitDiscountAuth,
      }))
    },
    CLEAN_CUSTOMER(state) {
      const { customer, customerId } = getInitialState().quotation
      state.quotation.customerId = customerId
      state.quotation.customer = customer
    },
  },

  actions: {
    async fetchQuotationById({ commit, dispatch }, id) {
      commit('CLEAN_STATE')
      const { data } = await axios.get(`/api/sales/quotations/${id}`)
      const initialState = getInitialState()
      const { FEE_PER_KILOMETER, SINGLE_FEE } = deliveryDomain.data().deliveryFeeEnum

      let delivery = {
        deliveryAddress: initialState.quotation.deliveryAddress,
        addressSelected: initialState.quotation.addressSelected,
      }

      const shippingValues = {
        shippingFee: data.deliveryFeeType === SINGLE_FEE ? data.deliveryTaxUnitValue : null,
        shippingFeePerKilometer:
          data.deliveryFeeType === FEE_PER_KILOMETER ? data.deliveryTaxUnitValue : null,
      }
      const deliveryData = {
        ...initialState.deliveryData,
        deliveryFeeType: data.deliveryFeeType || initialState.deliveryData.deliveryFeeType,
        distanceValue: data.deliveryTaxDistance || 0,
        deliveryTax: data.deliveryTax,
        ...shippingValues,
      }

      if (data.delivery) {
        const addressLabel = address.methods.formatAddress(data.deliveryAddress)
        const hasInAddressOptions = data.customer.addresses
          .map(ad => address.methods.formatAddress(ad))
          .findIndex(ad => ad === addressLabel)

        const addressSelected =
          hasInAddressOptions > -1
            ? addressUtils.makeAddressOption(data.deliveryAddress)
            : addressUtils.otherAddress()

        delivery = {
          deliveryAddress: data.deliveryAddress,
          addressSelected,
          deliveryTax: data.deliveryTax,
        }
      }

      const products = quotationItemUtils.buildQuotationItemsAndKitsFromApi(
        data.items,
        data.itemKits
      )
      const quotation = {
        id: data.id,
        storeId: String(data.store.id),
        customerId: stringUtils.methods.getStringOrNull(data.customer?.id),
        customer: {
          ...data.customer,
          label: data.customer.name,
        },
        discountType: data.discountType,
        discount: data.discount,
        isConsigned: data.consigned,
        priceTableId: String(data.priceTable.id),
        isDelivery: data.delivery,
        products,
        ...delivery,
        ...shippingValues,
      }

      commit('SET_QUOTATION', { quotation })
      commit('SET_DELIVERY_TAX', data.deliveryTax)
      commit('SET_DELIVERY_DATA', deliveryData)
      await dispatch('fetchStoreById', { storeId: quotation.storeId })
    },
    async fetchStoreById({ commit, dispatch }, { storeId }) {
      const { data: store } = await axios.get(`/api/stores/${storeId}`)

      // A taxa de delivery deixou de morar em `Store.shippingFees` e passou ao agregado
      // StoreDeliveryConfig, consumido pelo contrato administrativo. Resolvemos a config
      // vigente da loja para pré-preencher a taxa (única / por KM). Falha na leitura não
      // deve bloquear o fluxo de venda: as taxas caem para zero e o operador segue.
      // Config de delivery RESOLVIDA (própria-ou-Geral + horário + veto) para pré-preencher a taxa.
      let deliveryFees = { shippingFee: 0, shippingFeePerKilometer: 0, deliveryFeeType: null }
      try {
        const { data: resolved } = await axios.get(`/api/stores/${storeId}/delivery-resolved`)
        deliveryFees = {
          shippingFee: resolved.shippingFee ?? 0,
          shippingFeePerKilometer: resolved.shippingFeePerKilometer ?? 0,
          deliveryFeeType: resolved.deliveryFeeType ?? null,
          tiers: resolved.tiers || [],
          excludedGroupIds: resolved.excludedGroupIds || [],
        }
      } catch {
        deliveryFees = { shippingFee: 0, shippingFeePerKilometer: 0, deliveryFeeType: null }
      }
      store.deliveryFees = deliveryFees

      commit('SET_QUOTATION_STORE', { store })
      await dispatch('reloadDeliveryData')
    },
    async fetchCustomerById({ commit, state, dispatch }, { customerId }) {
      if (state.quotation.customer?.id?.toString() === customerId) return

      const { data } = await axios.get(`/api/customers/${customerId}/read-only`)
      commit('SET_QUOTATION_CUSTOMER', { customer: data })

      if (!state.quotation.id) {
        const validPriceTable = await dispatch(
          'common/priceTables/getPriceTableActiveOrDefaultId',
          { priceTableId: data?.priceTableId },
          { root: true }
        )
        commit('SET_QUOTATION_PRICE_TABLE_ID', { priceTableId: validPriceTable.id })
      }
    },

    setQuotationStoreId({ commit }, { storeId }) {
      commit('SET_QUOTATION_STORE_ID', { storeId })
    },
    setDeliveryAddress({ commit }, { addressData }) {
      commit('SET_DELIVERY_ADDRESS', { addressData })
    },

    async addProduct({ commit, state, dispatch }, { formData }) {
      const data = {
        ...formData,
      }
      const itemList = localListAddItem(state.quotation.products, data)

      commit('SET_PRODUCTS', { products: itemList })
      await dispatch('updateDeliveryTax')
    },
    async updateProduct({ commit, state, dispatch }, { formData }) {
      const data = {
        ...formData,
      }

      const itemList = localListUpdateItem(state.quotation.products, data)
      commit('SET_PRODUCTS', { products: itemList })
      await dispatch('updateDeliveryTax')
    },
    async removeProduct({ commit, state, dispatch }, { id }) {
      if (!id) throw new Error('Item sem id')
      const itemList = localListDeleteItem(state.quotation.products, id)

      commit('SET_PRODUCTS', { products: itemList })
      await dispatch('updateDeliveryTax')
    },

    async updateProductPrices({ state, commit }) {
      const { storeId, products, priceTableId } = state.quotation

      if (!storeId) return

      // TODO usar endpoint que mande lista de ean ao inves de fazer varias requests
      const productsPromise = products.map(async p => {
        const { data } = await axios.get(
          `/api/sales/pay-box-sku/store/${storeId}/ean/${p.skuProduct.ean}`,
          {
            params: {
              priceTableId,
              skipTaxValidation: true,
            },
          }
        )

        const refreshedQuotationItem = quotationItemUtils.quotationItemForceRefreshUsingProduct(
          p,
          data
        )

        return refreshedQuotationItem
      })

      const productsUpdated = await Promise.all(productsPromise)

      commit('SET_PRODUCTS', { products: productsUpdated })
    },

    reloadDeliveryData({ commit, state }) {
      const {
        shippingFee: storeShippingFee,
        shippingFeePerKilometer: storeShippingFeePerKilometer,
        tiers: storeTiers,
        excludedGroupIds: storeExcludedGroupIds,
      } = state.quotation.store.deliveryFees ?? {}
      commit('SET_DELIVERY_DATA', {
        ...state.deliveryData,
        shippingFee: state.quotation.shippingFee ?? storeShippingFee,
        shippingFeePerKilometer:
          state.quotation.shippingFeePerKilometer ?? storeShippingFeePerKilometer,
        tiers: storeTiers || [],
        excludedGroupIds: storeExcludedGroupIds || [],
      })
    },
    updateDeliveryTax({ state, commit, getters }) {
      const { FEE_PER_KILOMETER } = deliveryDomain.data().deliveryFeeEnum
      const { deliveryFeeType, distanceValue, shippingFee, shippingFeePerKilometer, tiers } =
        state.deliveryData
      let effectiveSingleFee = shippingFee
      let totalTax = shippingFee

      if (deliveryFeeType === FEE_PER_KILOMETER) {
        totalTax = roundDecimal(shippingFeePerKilometer * (distanceValue || 0))
      } else if (tiers && tiers.length) {
        // Cobrança única com faixas: aplica a faixa conforme o valor de produtos do orçamento,
        // desconsiderando itens de grupos isentos (D3/D15).
        const tierValue = selectDeliveryTierValue(tiers, getters.getDeliveryTierBaseValue)
        if (tierValue != null) {
          effectiveSingleFee = tierValue
          totalTax = tierValue
        }
      }

      // Reflete a faixa aplicada no campo exibido/gravado da taxa única.
      if (effectiveSingleFee !== shippingFee) {
        commit('SET_DELIVERY_SINGLE_FEE', effectiveSingleFee)
      }

      if (getters.hasDoubleDelivery) {
        totalTax *= 2
      }

      commit('SET_DELIVERY_TAX', totalTax)
    },

    async saveQuotation({ state }) {
      const {
        id,
        storeId,
        customer,
        priceTableId,
        products,
        discountType,
        discount,
        isDelivery,
        isConsigned,
        deliveryAddress,
      } = state.quotation
      const { deliveryData } = state

      const kit = products.filter(i => i.kitItems?.length > 0)
      const items = products
        .flatMap(item => {
          if (item.kitItems?.length > 0) return item.kitItems
          return [item]
        })
        .flatMap(item => {
          if (item.lendingProducts?.length > 0) return [item, ...item.lendingProducts]
          return [item]
        })
        .map(item => {
          const { skuProduct: itemSkuProduct, priceRuleSelected } = item
          const { priceInfo, priceTable } = priceRuleSelected

          return {
            id: item.id,
            skuId: itemSkuProduct.skuId,
            productKitId: itemSkuProduct?.productKitId,
            quotationItemKitId: item.quotationItemKitId,
            kitCreateUuid: item.kitCreateUuid,
            priceTableId: priceTable?.id,
            skuTieredPriceId: priceRuleSelected.tieredPrice?.id || null,
            skuAssociatedId: item.skuAssociatedId,
            quotationItemAssociatedId: item.quotationItemAssociatedId,
            promotionId: priceRuleSelected.promotion?.id || null,

            quantity: item.quantity,
            originalUnitValue: priceInfo.originalPrice,
            unitValue: priceInfo.unitValue,
            discountPriceTable: priceTable?.discount,
            discountType: priceInfo.discountType,
            unitDiscount: priceInfo.unitDiscount,

            isDeleted: item.isDeleted,
            contractualFine: priceInfo.contractualFine,
          }
        })

      let delivery = {}
      if (isDelivery) {
        delivery = {
          delivery: isDelivery,
          deliveryTax: deliveryData.deliveryTax,
          deliveryAddress: isDelivery ? deliveryAddress : null,
          deliveryFeeType: deliveryData.deliveryFeeType,
          deliveryTaxDistance: deliveryData.distanceValue,
          deliveryTaxUnitValue:
            deliveryData.deliveryFeeType === deliveryDomain.data().deliveryFeeEnum.FEE_PER_KILOMETER
              ? deliveryData.shippingFeePerKilometer
              : deliveryData.shippingFee,
        }
      }

      const body = {
        id,
        storeId,
        priceTableId,
        customerId: customer.id,
        discountType,
        discount,
        items,
        itemKits: kit.map(k => ({
          productKitId: k.skuProduct.id,
          unitValue: k.priceRuleSelected.priceInfo.unitValue,
          quantity: k.quantity,
          kitCreateUuid: k.kitCreateUuid,
        })),
        consigned: isConsigned,
        ...delivery,
      }

      await axios({
        url: '/api/sales/quotations',
        method: 'POST',
        data: body,
      })
    },

    cleanState({ commit }) {
      commit('CLEAN_STATE')
    },
    cleanDeliveryAddress({ commit }) {
      commit('CLEAN_DELIVERY_ADDRESS')
    },
    cleanInputsAuth({ commit }) {
      // TODO melhoria: verificar se usuário que autorizou campos na loja possui permissão na outra loja.
      commit('CLEAN_INPUTS_AUTH')
    },
    cleanCustomer({ commit }) {
      commit('CLEAN_CUSTOMER')
    },
  },
}
